// ============================================================
// Standortmonitor — Library — Slice Preview Builders
// Chart-family reducers: transformed DSV rows → SlicePreviewSeries[].
// Selected via SLICE_SHELL_REGISTRY.previewBuilder.
// ============================================================

import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { DSVRowArray } from 'd3';
import { DashboardUtils } from 'src/app/dashboards/DashboardUtils';
import { Granularity } from 'src/app/dashboards/shared/dashboard-config/dashboard-config.model';
import { LocalDatasetsService } from 'src/app/shared/services/local-datasets.service';
import { formatWzId } from 'src/app/shared/utils/format-wz-id';
import { SlicePreviewCol, SlicePreviewSeries } from 'src/app/state/library/library.model';
import {
  COMPARISON_GRID_ROW_KEY,
  COMPARISON_GRIDS_KEY,
  SLICE_SANKEY_OTHERS_CODE,
  SLICE_SANKEY_SOURCE_CODE,
} from 'src/app/state/library/library.util';
import { LocalDatasetsState } from 'src/app/state/local_datasets/local_datasets.state';
import {
  formatCategoryLabel,
  hasKEurFilterValue,
  isAnonPreviewCell,
  MAX_PREVIEW_SERIES,
  MAX_PREVIEW_VALUES,
  pickValueKey,
  readFilterValue,
  resolveCategoryPair,
  TIME_KEYS,
  VALUE_KEYS,
} from './preview-series.helpers';
import { getSliceShellEntry, SlicePreviewBuilderId } from './slice-definition-shell';

/** Top non-focus states shown before focus + “Übrige” in the Sankey card. */
const SANKEY_TOP_N = 4;
/** Top states kept for the WZ rank matrix card (full mode). Compact UI trims further. */
const WZRANK_TOP_N = 6;
const WZRANK_SUM_CODE = 'Σ';
const WZRANK_SUM_LABEL = 'Summe Teilmarkt';
/** Stack segments kept for forecast-structure library cards (Variant B density). */
const FORECAST_STACK_TOP_N = 6;
/** Entities kept for index-ranking-development slope cards (NVDuoB density). */
const SLOPE_TOP_N = 6;

@Injectable({ providedIn: 'root' })
export class SlicePreviewBuildersService {
  constructor(
    private store: Store,
    private localDatasetsService: LocalDatasetsService,
  ) {}

  /**
   * Reduce transformed chart data to a preview series.
   * Dedicated builders are selected via `SLICE_SHELL_REGISTRY.previewBuilder`.
   */
  buildPreviewSeries(
    data: DSVRowArray | null,
    dashboardId: string,
    filterValues?: Map<string, unknown> | Record<string, unknown>,
    shellId?: string,
  ): SlicePreviewSeries[] {
    if (!data || data.length === 0) {
      return [];
    }

    const builderId = getSliceShellEntry(shellId)?.previewBuilder ?? 'generic';
    return this._runPreviewBuilder(builderId, data, filterValues, dashboardId, shellId);
  }

  private _runPreviewBuilder(
    builderId: SlicePreviewBuilderId,
    data: DSVRowArray,
    filterValues: Map<string, unknown> | Record<string, unknown> | undefined,
    dashboardId: string,
    shellId?: string,
  ): SlicePreviewSeries[] {
    switch (builderId) {
      case 'generic':
        return this._buildGenericPreview(data, dashboardId, filterValues, shellId);
      case 'sankey':
        return this.buildSankeyPreview(data, filterValues);
      case 'onion':
        return this.buildOnionPreview(data, filterValues);
      case 'line':
        return this.buildLinePreview(data, filterValues);
      case 'wzrank':
        return this.buildWzRankPreview(data, filterValues);
      case 'quad-line':
        try {
          const { granularityId } = DashboardUtils.decodeDashboardId(dashboardId);
          return this.buildQuadLinePreview(data, filterValues, granularityId);
        } catch {
          return [];
        }
      case 'duel':
        return this.buildDuelPreview(data, filterValues);
      case 'forecast-line':
        return this.buildForecastPreview(data, filterValues);
      case 'forecast-stack':
        return this.buildForecastStackPreview(data, filterValues, dashboardId);
      case 'slope':
        return this.buildSlopePreview(data, filterValues, dashboardId);
    }
  }

  /** Generic category × time reducer (bar / ranking shells without a dedicated builder). */
  private _buildGenericPreview(
    data: DSVRowArray,
    dashboardId: string,
    filterValues?: Map<string, unknown> | Record<string, unknown>,
    shellId?: string,
  ): SlicePreviewSeries[] {
    const sample = data[0] as Record<string, unknown>;
    const categoryPair = resolveCategoryPair(sample, dashboardId, shellId);
    const timeKey = TIME_KEYS.find((key) => key in sample);
    const valueKey = pickValueKey(sample, categoryPair, timeKey);
    const kEurScale = hasKEurFilterValue(filterValues) ? 1000 : 1;

    if (!valueKey) {
      return [];
    }

    const grouped = new Map<string, { name: string; points: { t: number; v: number }[] }>();

    data.forEach((row: any, index: number) => {
      const code = categoryPair ? `${row[categoryPair.code]}` : 'all';
      const name = categoryPair ? `${row[categoryPair.name] ?? row[categoryPair.code]}` : 'Gesamt';
      const raw = Number(row[valueKey]);
      if (!Number.isFinite(raw)) {
        return;
      }
      const value = raw * kEurScale;
      const t = timeKey ? Number(row[timeKey]) : index;

      if (!grouped.has(code)) {
        grouped.set(code, { name, points: [] });
      }
      grouped.get(code)!.points.push({ t: Number.isFinite(t) ? t : index, v: value });
    });

    return Array.from(grouped.entries())
      .slice(0, MAX_PREVIEW_SERIES)
      .map(([code, { name, points }]) => {
        const vals = points
          .sort((a, b) => a.t - b.t)
          .map((p) => p.v)
          .slice(-MAX_PREVIEW_VALUES);
        return { code, name, vals };
      })
      .filter((series) => series.vals.length > 0);
  }

  /**
   * WZ multi-column ranking matrix (NVWzRankA): Σ first, then filter-selected
   * WZ columns; top states by Σ; ranks over all states for the blue ramp.
   * Card uses full (top-6 + all cols); board tile trims via compact input.
   */
  buildWzRankPreview(
    data: DSVRowArray,
    filterValues?: Map<string, unknown> | Record<string, unknown>,
  ): SlicePreviewSeries[] {
    const rows = Array.from(data as Iterable<Record<string, unknown>>);
    if (!rows.length) {
      return [];
    }

    const idCodeMap = this.localDatasetsService.getStateIdCodeMap();
    type StateAgg = {
      code: string;
      name: string;
      sum: number;
      byWz: Map<string, number>;
    };
    const byState = new Map<string, StateAgg>();
    const wzLabels = new Map<string, string>();
    const wzSeenOrder: string[] = [];

    const parseMetric = (raw: unknown): number => {
      const n = Number(raw);
      return Number.isFinite(n) ? n : NaN;
    };

    for (const row of rows) {
      const stateId = row['state_id'];
      if (stateId == null || stateId === '') {
        continue;
      }
      const stateKey = String(stateId);
      const wzId = row['wz_id'];
      if (wzId == null || wzId === '') {
        continue;
      }
      const wzKey = String(wzId);
      if (!wzLabels.has(wzKey)) {
        wzLabels.set(wzKey, String(row['wz_name'] ?? formatWzId(wzKey)));
        wzSeenOrder.push(wzKey);
      }

      let state = byState.get(stateKey);
      if (!state) {
        const code =
          (row['state_code'] != null && row['state_code'] !== ''
            ? String(row['state_code'])
            : idCodeMap.get(Number(stateId))) ?? stateKey;
        state = {
          code,
          name: String(row['state_name'] ?? code),
          sum: parseMetric(row['value_sum']),
          byWz: new Map(),
        };
        byState.set(stateKey, state);
      } else if (!Number.isFinite(state.sum)) {
        state.sum = parseMetric(row['value_sum']);
      }
      state.byWz.set(wzKey, parseMetric(row['value']));
    }

    if (!byState.size) {
      return [];
    }

    const filterWz = readFilterValue(filterValues, 'wz_classes');
    const filterOrder: string[] = Array.isArray(filterWz)
      ? filterWz.map((id) => String(id))
      : filterWz != null && filterWz !== ''
        ? [String(filterWz)]
        : [];

    const wzOrder: string[] = [];
    for (const id of filterOrder) {
      if (wzLabels.has(id) && !wzOrder.includes(id)) {
        wzOrder.push(id);
      }
    }
    for (const id of wzSeenOrder) {
      if (!wzOrder.includes(id)) {
        wzOrder.push(id);
      }
    }

    const cols: SlicePreviewCol[] = [
      { code: WZRANK_SUM_CODE, label: WZRANK_SUM_LABEL },
      ...wzOrder.map((id) => ({ code: formatWzId(id), label: wzLabels.get(id)! })),
    ];

    const states = Array.from(byState.values());
    const rankPool = Math.max(states.length - 1, 1);

    const rankByCode = (values: { code: string; v: number }[]): Map<string, number> => {
      const ranked = [...values].sort((a, b) => {
        const av = Number.isFinite(a.v) ? a.v : Number.NEGATIVE_INFINITY;
        const bv = Number.isFinite(b.v) ? b.v : Number.NEGATIVE_INFINITY;
        return bv - av;
      });
      const map = new Map<string, number>();
      ranked.forEach((r, i) => map.set(r.code, i + 1));
      return map;
    };

    const sumRanks = rankByCode(states.map((s) => ({ code: s.code, v: s.sum })));
    const wzRanks = new Map<string, Map<string, number>>();
    for (const wzId of wzOrder) {
      wzRanks.set(
        wzId,
        rankByCode(states.map((s) => ({ code: s.code, v: s.byWz.get(wzId) ?? NaN }))),
      );
    }

    const top = [...states]
      .sort((a, b) => {
        const av = Number.isFinite(a.sum) ? a.sum : Number.NEGATIVE_INFINITY;
        const bv = Number.isFinite(b.sum) ? b.sum : Number.NEGATIVE_INFINITY;
        return bv - av;
      })
      .slice(0, WZRANK_TOP_N);

    return top.map((s) => {
      const rawVals = [
        s.sum,
        ...wzOrder.map((wzId) => s.byWz.get(wzId) ?? NaN),
      ];
      const anon = rawVals.map((v) => !Number.isFinite(v));
      const vals = rawVals.map((v, i) => (anon[i] ? 0 : v));
      const ranks = [
        sumRanks.get(s.code) ?? states.length,
        ...wzOrder.map((wzId) => wzRanks.get(wzId)?.get(s.code) ?? states.length),
      ];
      return {
        code: s.code,
        name: s.name,
        vals,
        ranks,
        anon,
        cols,
        rankPool,
      };
    });
  }

  /**
   * A/B district comparison duel (NVDuel): three Teilmarkt metrics summed
   * per side from selected districts; region labels from multiselects.
   *
   * Row selection uses `districts_multiselect_{a|b}` from the recipe (not
   * `pass_*` stamp columns). Anonymized (`*`) cells mark the side anon;
   * missing / empty / unmatched districts stay numeric 0 (not anon) so the
   * duel card does not gray out the other side's bars.
   */
  buildDuelPreview(
    data: DSVRowArray,
    filterValues?: Map<string, unknown> | Record<string, unknown>,
  ): SlicePreviewSeries[] {
    const rows = Array.from(data as Iterable<Record<string, unknown>>);
    const cols: SlicePreviewCol[] = [
      { code: 'total', label: 'Beschäftigte ges.' },
      { code: 'social', label: 'Sv-pflichtig' },
      { code: 'marginal', label: 'Geringfügig' },
    ];
    const metricKeys = [
      'employed_total_sub_market_district',
      'employed_social_security_sub_market_district',
      'employed_marginal_sub_market_district',
    ] as const;

    const sideA = this._buildDuelSide('A', rows, filterValues, metricKeys, cols);
    const sideB = this._buildDuelSide('B', rows, filterValues, metricKeys, cols);
    return [sideA, sideB];
  }

  private _buildDuelSide(
    side: 'A' | 'B',
    rows: Record<string, unknown>[],
    filterValues: Map<string, unknown> | Record<string, unknown> | undefined,
    metricKeys: readonly string[],
    cols: SlicePreviewCol[],
  ): SlicePreviewSeries {
    const sideKey = side.toLowerCase();
    const districtIds = this._duelDistrictIds(filterValues, sideKey);
    const sideRows = this._duelSideRows(rows, sideKey, districtIds);

    const vals: number[] = [];
    const anon: boolean[] = [];
    for (const key of metricKeys) {
      let sum = 0;
      let sawAnonMarker = false;
      for (const row of sideRows) {
        const raw = row[key];
        if (raw == null || raw === '') {
          continue;
        }
        const asStr = String(raw).trim();
        // Only explicit anonymization markers — empty / missing / zero sides
        // must stay numeric 0 (same as the live map body). Treating "no
        // finite values" as anon made the card gray out *both* duel bars
        // (aAnon || bAnon) while Analyse still showed A=0 and B correctly.
        if (asStr === '*' || asStr === '–' || asStr === '-') {
          sawAnonMarker = true;
          break;
        }
        const n = Number(raw);
        if (!Number.isFinite(n)) {
          continue;
        }
        sum += n;
      }
      if (sawAnonMarker) {
        vals.push(0);
        anon.push(true);
      } else {
        vals.push(sum);
        anon.push(false);
      }
    }

    const { name, shortName } = this._duelRegionLabels(side, filterValues);
    return { code: side, name, shortName, vals, anon, cols };
  }

  private _duelDistrictIds(
    filterValues: Map<string, unknown> | Record<string, unknown> | undefined,
    sideKey: string,
  ): string[] {
    const raw = readFilterValue(filterValues, `districts_multiselect_${sideKey}`);
    if (Array.isArray(raw)) {
      return raw.map((id) => String(id)).filter((id) => id !== '');
    }
    if (raw != null && raw !== '') {
      return [String(raw)];
    }
    return [];
  }

  /** Prefer recipe district ids; fall back to pass_* stamps for older payloads. */
  private _duelSideRows(
    rows: Record<string, unknown>[],
    sideKey: string,
    districtIds: string[],
  ): Record<string, unknown>[] {
    if (districtIds.length) {
      const idSet = new Set(districtIds);
      return rows.filter((row) => idSet.has(String(row['district_id'] ?? '')));
    }
    const statePass = `pass_states_multiselect_${sideKey}`;
    const districtPass = `pass_districts_multiselect_${sideKey}`;
    return rows.filter(
      (row) => `${row[statePass]}` === 'true' && `${row[districtPass]}` === 'true',
    );
  }

  private _duelRegionLabels(
    side: 'A' | 'B',
    filterValues: Map<string, unknown> | Record<string, unknown> | undefined,
  ): { name: string; shortName: string } {
    const districtIds = this._duelDistrictIds(filterValues, side.toLowerCase());

    const districtsMap = this.localDatasetsService.getDistrictObjectMap();
    const statesMap = this.localDatasetsService.getStatesMap();
    const stateCodeMap = this.localDatasetsService.getStateIdCodeMap();

    type DistLabel = { name: string; stateCode: string; stateName: string };
    const labels: DistLabel[] = [];
    for (const id of districtIds) {
      const district = districtsMap.get(+id);
      if (!district) {
        continue;
      }
      const stateId = Number(district['state_id']);
      labels.push({
        name: String(district['name'] ?? id),
        stateName: statesMap.get(stateId) ?? '',
        stateCode: stateCodeMap.get(stateId) ?? String(stateId),
      });
    }
    labels.sort((a, b) => {
      if (a.stateName < b.stateName) return -1;
      if (a.stateName > b.stateName) return 1;
      if (a.name < b.name) return -1;
      if (a.name > b.name) return 1;
      return 0;
    });

    if (!labels.length) {
      return { name: side, shortName: side };
    }

    const name = labels.map((d) => d.name).join(' + ');
    const codes = [...new Set(labels.map((d) => d.stateCode))];
    const shortName =
      labels.length <= 2
        ? labels.map((d) => this._shortDistrictToken(d.name, d.stateCode)).join(' + ')
        : `${codes.join('+')} (${labels.length})`;
    return { name, shortName };
  }

  private _shortDistrictToken(districtName: string, stateCode: string): string {
    const trimmed = districtName.trim();
    if (trimmed.length <= 10) {
      return trimmed;
    }
    // Prefer leading word + state code (e.g. "München …" → "M …" style density).
    const first = trimmed.split(/\s+/)[0] ?? trimmed;
    if (first.length <= 8) {
      return `${first} ${stateCode}`;
    }
    return `${first.slice(0, 1)} ${stateCode}`;
  }

  /**
   * Four-grid comparison trend preview (quad-line): one spark per
   * quadrant. Values are the sum of all selected rows in that quadrant per
   * year/period. Quadrant order comes from persisted `comparison_grids`.
   * Save-time merges stamp `__comparison_grid_id`; live refresh matches
   * `sub_market_id` / `state_id` instead.
   */
  buildQuadLinePreview(
    data: DSVRowArray,
    filterValues: Map<string, unknown> | Record<string, unknown> | undefined,
    granularityId: Granularity,
  ): SlicePreviewSeries[] {
    const rows = Array.from(data as Iterable<Record<string, unknown>>);
    if (!rows.length) {
      return [];
    }

    const gridIds = this._readComparisonGrids(filterValues);
    if (!gridIds.length) {
      return [];
    }

    const { codeKey, nameKey } = this._comparisonGridKeys(granularityId);
    const timeKey = this._comparisonTimeKey(rows[0]);
    const sumIds = new Set(
      this.store.selectSnapshot(LocalDatasetsState.sumSubMarketIds).map((id) => `${id}`),
    );

    const nameResolvers: Record<string, (id: string) => string | undefined> = {
      sub_market_id: (id) => this.localDatasetsService.getSubmarketMap().get(Number(id)),
      state_id: (id) => this.localDatasetsService.getStatesMap().get(Number(id)),
    };

    return gridIds.map((gridId) => {
      const gridRows = rows.filter((row) => {
        const stamped = row[COMPARISON_GRID_ROW_KEY];
        if (stamped != null && stamped !== '') {
          return `${stamped}` === gridId;
        }
        return `${row[codeKey] ?? ''}` === gridId;
      });
      const byTime = new Map<number, number>();
      let finiteCount = 0;

      for (const row of gridRows) {
        const t = Number(row[timeKey]);
        const value = Number(row['value']);
        if (!Number.isFinite(t) || !Number.isFinite(value)) {
          continue;
        }
        finiteCount += 1;
        byTime.set(t, (byTime.get(t) ?? 0) + value);
      }

      const years = Array.from(byTime.keys())
        .sort((a, b) => a - b)
        .slice(-MAX_PREVIEW_VALUES);
      const vals = years.map((y) => byTime.get(y)!);

      const sampleName = gridRows.find((row) => row[nameKey] != null)?.[nameKey];
      const resolved = nameResolvers[codeKey]?.(gridId);
      const name = sampleName != null ? `${sampleName}` : resolved ?? gridId;
      const isSum = sumIds.has(gridId) || name.trim().startsWith('Σ') || name.trim().startsWith('∑');

      if (!vals.length) {
        return {
          code: gridId,
          name,
          vals: [],
          years: [],
          isSum,
          anon: finiteCount === 0 ? [true] : undefined,
        };
      }

      return { code: gridId, name, vals, years, isSum };
    });
  }

  private _readComparisonGrids(
    filterValues: Map<string, unknown> | Record<string, unknown> | undefined,
  ): string[] {
    const raw = readFilterValue(filterValues, COMPARISON_GRIDS_KEY);
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw.map((id) => `${id}`).filter((id) => id !== '');
  }

  /** Data column that identifies each comparison quadrant. */
  private _comparisonGridKeys(granularityId: Granularity): { codeKey: string; nameKey: string } {
    if (granularityId === Granularity.States) {
      return { codeKey: 'sub_market_id', nameKey: 'sub_market_name' };
    }
    return { codeKey: 'state_id', nameKey: 'state_name' };
  }

  private _comparisonTimeKey(sample: Record<string, unknown>): string {
    return TIME_KEYS.find((key) => key in sample) ?? 'year';
  }

  /**
   * Sankey preview: picks the first parent→child edge in the data
   * (following the Sankey node hierarchy), then condenses targets to
   * top-N (excl. focus) + focus node + “Übrige” (incl. anonymized).
   */
  buildSankeyPreview(
    data: DSVRowArray,
    filterValues?: Map<string, unknown> | Record<string, unknown>,
  ): SlicePreviewSeries[] {
    const rows = Array.from(data as Iterable<Record<string, unknown>>);
    const edge = this._pickSankeyEdge(rows);
    if (!edge) {
      return [];
    }

    const focusCode = this._resolveSankeyFocusCode(edge.targetType, filterValues);
    const nameResolver = this._sankeyNameResolver(edge.targetType);

    let anonSum = 0;
    let anonCount = 0;
    const byCode = new Map<string, { code: string; name: string; v: number }>();

    for (const row of rows) {
      if (row['source_type'] !== edge.sourceType || row['target_type'] !== edge.targetType) {
        continue;
      }

      const value = Number(row['value']);
      if (!Number.isFinite(value)) {
        continue;
      }

      const targetId = row['target_id'];
      if (targetId == null || targetId === '') {
        anonSum += value;
        anonCount += 1;
        continue;
      }

      const code = nameResolver.code(targetId);
      const name = `${row['target_name'] ?? ''}` || nameResolver.name(targetId) || code;
      const existing = byCode.get(code);
      if (existing) {
        existing.v += value;
      } else {
        byCode.set(code, { code, name, v: value });
      }
    }

    const ranked = Array.from(byCode.values()).sort((a, b) => b.v - a.v);
    const focus = focusCode ? ranked.find((r) => r.code === focusCode) : undefined;
    const top = ranked.slice(0, SANKEY_TOP_N).filter((r) => r.code !== focusCode);
    const rest = ranked.filter((r) => !top.includes(r) && r !== focus);
    const restSum = rest.reduce((sum, r) => sum + r.v, 0) + anonSum;
    const restCount = rest.length + anonCount;

    const sourceLabel = this._sankeySourceLabel(edge.sourceType, rows, edge);

    const nodes: SlicePreviewSeries[] = [
      {
        code: SLICE_SANKEY_SOURCE_CODE,
        name: sourceLabel,
        shortName: `${edge.sourceType}|${edge.targetType}`,
        vals: [],
      },
    ];

    nodes.push(...top.map((r) => ({
      code: r.code,
      name: r.name,
      vals: [r.v],
    })));

    if (focus) {
      nodes.push({ code: focus.code, name: focus.name, vals: [focus.v] });
    }

    if (restCount > 0) {
      nodes.push({
        code: SLICE_SANKEY_OTHERS_CODE,
        name: `Übrige (${restCount})`,
        vals: [restSum],
      });
    }

    return nodes;
  }

  /**
   * Pick the first source→target edge type present in the data,
   * following the Sankey node hierarchy (country → state → industry_sub_market → …).
   */
  private _pickSankeyEdge(
    rows: Record<string, unknown>[],
  ): { sourceType: string; targetType: string } | undefined {
    const hierarchy = ['country', 'state', 'industry_sub_market', 'sub_market', 'wz'];
    const edgeTypes = new Set(
      rows.map((r) => `${r['source_type']}|${r['target_type']}`),
    );
    for (let i = 0; i < hierarchy.length - 1; i++) {
      if (edgeTypes.has(`${hierarchy[i]}|${hierarchy[i + 1]}`)) {
        return { sourceType: hierarchy[i], targetType: hierarchy[i + 1] };
      }
    }
    const first = rows.find((r) => r['source_type'] && r['target_type']);
    if (first) {
      return { sourceType: String(first['source_type']), targetType: String(first['target_type']) };
    }
    return undefined;
  }

  /** Resolve focus code for highlight, scoped to the target entity type. */
  private _resolveSankeyFocusCode(
    targetType: string,
    filterValues?: Map<string, unknown> | Record<string, unknown>,
  ): string | undefined {
    if (targetType === 'state') {
      const stateId = readFilterValue(filterValues, 'state');
      if (stateId != null && stateId !== '') {
        return this.localDatasetsService.getStateIdCodeMap().get(Number(stateId));
      }
    }
    return undefined;
  }

  /** Name resolver for Sankey target nodes per entity type. */
  private _sankeySourceLabel(
    sourceType: string,
    rows: Record<string, unknown>[],
    edge: { sourceType: string; targetType: string },
  ): string {
    if (sourceType === 'country') {
      return 'Deutschland';
    }
    const sourceRow = rows.find(
      (r) => r['source_type'] === edge.sourceType && r['target_type'] === edge.targetType,
    );
    if (!sourceRow) {
      return String(sourceType);
    }
    const sourceId = sourceRow['source_id'];
    const resolver = this._sankeyNameResolver(sourceType);
    return resolver.name(sourceId) || resolver.code(sourceId);
  }

  private _sankeyNameResolver(
    targetType: string,
  ): { code: (id: unknown) => string; name: (id: unknown) => string } {
    switch (targetType) {
      case 'state': {
        const codeMap = this.localDatasetsService.getStateIdCodeMap();
        const nameMap = this.localDatasetsService.getStatesMap();
        return {
          code: (id) => codeMap.get(Number(id)) ?? String(id),
          name: (id) => nameMap.get(Number(id)) ?? '',
        };
      }
      case 'sub_market': {
        const nameMap = this.localDatasetsService.getSubmarketMap();
        return {
          code: (id) => String(id),
          name: (id) => nameMap.get(Number(id)) ?? '',
        };
      }
      case 'industry_sub_market': {
        const nameMap = this.localDatasetsService.getIndustryIdNameMap();
        return {
          code: (id) => String(id),
          name: (id) => nameMap.get(Number(id)) ?? '',
        };
      }
      default:
        return {
          code: (id) => String(id),
          name: () => '',
        };
    }
  }

  /**
   * Onion preview: nested levels from filtered dossier rows (country → … → sub_market).
   * Mirrors ChartOnionComponent.buildChartData but drops WZ and emits SlicePreviewSeries[].
   */
  buildOnionPreview(
    data: DSVRowArray,
    filterValues?: Map<string, unknown> | Record<string, unknown>,
  ): SlicePreviewSeries[] {
    const stateId = readFilterValue(filterValues, 'state');
    const subMarketId = readFilterValue(filterValues, 'sub_market');
    const wzVisibility = readFilterValue(filterValues, 'wz_visibility');
    const wzClass = readFilterValue(filterValues, 'wz_class');

    const rows = Array.from(data as Iterable<Record<string, unknown>>);
    const chartRows = rows.filter(
      (row) =>
        (row['target_type'] === 'state' && `${row['target_id']}` === `${stateId}`) ||
        (row['target_type'] === 'sub_market' && `${row['target_id']}` === `${subMarketId}`) ||
        (wzVisibility === 'true' &&
          row['target_type'] === 'wz' &&
          `${row['target_id']}` === `${wzClass}`),
    );

    const stateSample = chartRows.find((row) => row['source_type'] === 'country');
    if (stateSample) {
      chartRows.push({
        target_type: 'country',
        target_id: '-1',
        target_name: 'Deutschland',
        value: stateSample['value_parent'],
      });
    }

    const subMarketSample = chartRows.find((row) => row['target_type'] === 'sub_market');
    if (subMarketSample) {
      let industrySubMarket = rows.find(
        (row) =>
          row['target_type'] === 'industry_sub_market' &&
          `${row['target_id']}` === `${subMarketSample['source_id']}`,
      );
      if (!industrySubMarket) {
        industrySubMarket = {
          source_id: stateId,
          source_type: 'state',
          target_type: 'industry_sub_market',
          target_id: subMarketSample['source_id'],
          target_name: subMarketSample['source_name'],
          value: subMarketSample['value_parent'],
        };
      }
      chartRows.push(industrySubMarket);
    }

    const targetTypeOrder = ['country', 'state', 'industry_sub_market', 'sub_market', 'wz'];
    chartRows.sort(
      (a, b) =>
        targetTypeOrder.indexOf(String(a['target_type'])) -
        targetTypeOrder.indexOf(String(b['target_type'])),
    );

    const levels: SlicePreviewSeries[] = [];
    for (const row of chartRows) {
      if (row['target_type'] === 'wz') {
        continue;
      }
      const value = Number(row['value']);
      levels.push({
        code: `${row['target_type']}_${row['target_id']}`,
        name: `${row['target_name'] ?? row['target_id']}`,
        vals: Number.isFinite(value) && value > 0 ? [value] : [],
      });
    }

    return levels;
  }

  /**
   * Entwicklung line preview: one series for the selected state over years.
   * Prefers Σ (sum) sub-market rows — same filter as
   * ChartDossierDevelopmentTabComponent.handleDataChange — and falls back to
   * state-level rows when no sum-submarket data is present.
   */
  buildLinePreview(
    data: DSVRowArray,
    filterValues?: Map<string, unknown> | Record<string, unknown>,
  ): SlicePreviewSeries[] {
    const sumIds = new Set(
      this.store.selectSnapshot(LocalDatasetsState.sumSubMarketIds).map((id) => `${id}`),
    );
    const stateId = readFilterValue(filterValues, 'state');
    const idCodeMap = this.localDatasetsService.getStateIdCodeMap();
    const idNameMap = this.localDatasetsService.getStatesMap();
    const code =
      stateId != null && stateId !== ''
        ? idCodeMap.get(Number(stateId)) ?? `${stateId}`
        : 'all';
    const name =
      stateId != null && stateId !== ''
        ? idNameMap.get(Number(stateId)) ?? code
        : 'Gesamt';

    const rows = Array.from(data as Iterable<Record<string, unknown>>);
    const sumRows = rows.filter((row) => sumIds.has(`${row['sub_market_id']}`));
    const stateRows =
      stateId != null && stateId !== ''
        ? rows.filter((row) => `${row['state_id']}` === `${stateId}`)
        : rows;
    // Prefer Σ-submarket grain (chart parity); otherwise use state rows as-is.
    const source = sumRows.length > 0 ? sumRows : stateRows;

    const byYear = new Map<number, number>();
    for (const row of source) {
      const year = Number(row['year']);
      const value = Number(row['value']);
      if (!Number.isFinite(year) || !Number.isFinite(value)) {
        continue;
      }
      byYear.set(year, (byYear.get(year) ?? 0) + value);
    }

    const years = Array.from(byYear.keys())
      .sort((a, b) => a - b)
      .slice(-MAX_PREVIEW_VALUES);
    const vals = years.map((y) => byYear.get(y)!);
    if (!vals.length) {
      return [];
    }

    return [{ code, name, vals, years }];
  }

  /**
   * Forecast comparison tunnel (NVForecast): one focus series with history
   * values, forecast values, and CI band (lo/hi). Forecast years are rows
   * with a truthy `forecast_method_id` (same split as chart-line-forecast).
   */
  buildForecastPreview(
    data: DSVRowArray,
    filterValues?: Map<string, unknown> | Record<string, unknown>,
    dashboardId?: string,
    dashboardVariant?: string,
  ): SlicePreviewSeries[] {
    const rows = Array.from(data as Iterable<Record<string, unknown>>);
    if (!rows.length) {
      return [];
    }

    const sample = rows[0];
    const categoryPair = resolveCategoryPair(
      sample,
      dashboardId ?? '',
      dashboardVariant,
    );
    const focusId = this._resolveForecastFocusId(filterValues);
    let focused = rows;
    if (categoryPair && focusId != null && focusId !== '') {
      const matched = rows.filter((row) => `${row[categoryPair.code]}` === `${focusId}`);
      if (matched.length) {
        focused = matched;
      }
    }
    if (categoryPair && focused === rows) {
      const firstCode = `${rows[0][categoryPair.code]}`;
      focused = rows.filter((row) => `${row[categoryPair.code]}` === firstCode);
    }

    const { valueKey, upperKey, lowerKey } = this._resolveForecastValueKeys(sample, categoryPair);
    if (!valueKey) {
      return [];
    }

    type YearPoint = { value: number; lo: number | null; hi: number | null; isForecast: boolean };
    const byYear = new Map<number, YearPoint>();
    for (const row of focused) {
      const year = Number(row['year']);
      const value = Number(row[valueKey]);
      if (!Number.isFinite(year) || !Number.isFinite(value)) {
        continue;
      }
      const isForecast = !!row['forecast_method_id'];
      let lo: number | null = null;
      let hi: number | null = null;
      // Blank / anonymized CI cells must stay null — Number('') is 0, which
      // would fake a band collapsing to zero.
      if (
        isForecast &&
        upperKey &&
        lowerKey &&
        !isAnonPreviewCell(row[lowerKey]) &&
        !isAnonPreviewCell(row[upperKey])
      ) {
        lo = Number(row[lowerKey]);
        hi = Number(row[upperKey]);
      }
      byYear.set(year, { value, lo, hi, isForecast });
    }

    const years = Array.from(byYear.keys())
      .sort((a, b) => a - b)
      .slice(-MAX_PREVIEW_VALUES);
    if (!years.length) {
      return [];
    }

    const vals = years.map((y) => byYear.get(y)!.value);
    const lo = years.map((y) => byYear.get(y)!.lo);
    const hi = years.map((y) => byYear.get(y)!.hi);
    // Forecast split comes from the row flag, so datasets without CI columns
    // still render dashed forecast years.
    const isFc = years.map((y) => byYear.get(y)!.isForecast);

    const first = focused[0];
    const code = categoryPair ? `${first[categoryPair.code]}` : 'focus';
    let name = categoryPair
      ? `${first[categoryPair.name] ?? first[categoryPair.code]}`
      : 'Gesamt';
    if (categoryPair?.code === 'wz_id' || categoryPair?.code === 'wz_class_id') {
      const wzLabel = formatWzId(`${first[categoryPair.code]}`);
      const wzName = first[categoryPair.name];
      name = wzName ? `${wzLabel} ${wzName}` : wzLabel || name;
    }

    return [{ code, name, vals, years, lo, hi, isFc }];
  }

  /**
   * Forecast-structure stacked columns (NVForecastStack Variant B): one series
   * per stack segment (WZ / Teilmarkt), shared years + isFc mask. No YoY.
   */
  buildForecastStackPreview(
    data: DSVRowArray,
    _filterValues?: Map<string, unknown> | Record<string, unknown>,
    dashboardId?: string,
  ): SlicePreviewSeries[] {
    const rows = Array.from(data as Iterable<Record<string, unknown>>);
    if (!rows.length) {
      return [];
    }

    const sample = rows[0];
    const categoryPair = resolveCategoryPair(sample, dashboardId ?? '');
    if (!categoryPair) {
      return [];
    }

    const valueKey = pickValueKey(
      sample,
      categoryPair,
      TIME_KEYS.find((key) => key in sample),
    );
    if (!valueKey) {
      return [];
    }

    type Cell = { value: number; anon: boolean; isFc: boolean };
    const byCode = new Map<string, { name: string; byYear: Map<number, Cell> }>();
    const yearFc = new Map<number, boolean>();

    for (const row of rows) {
      const year = Number(row['year']);
      if (!Number.isFinite(year)) {
        continue;
      }
      const code = `${row[categoryPair.code] ?? ''}`.trim();
      if (!code) {
        continue;
      }

      const isFc = !!row['forecast_method_id'];
      if (isFc) {
        yearFc.set(year, true);
      } else if (!yearFc.has(year)) {
        yearFc.set(year, false);
      }

      const raw = row[valueKey];
      const cellAnon = isAnonPreviewCell(raw);
      const value = Number(raw);

      let entry = byCode.get(code);
      if (!entry) {
        entry = {
          name: formatCategoryLabel(row, categoryPair, code),
          byYear: new Map(),
        };
        byCode.set(code, entry);
      }

      const prev = entry.byYear.get(year);
      if (!prev) {
        entry.byYear.set(year, {
          value: cellAnon ? 0 : value,
          anon: cellAnon,
          isFc,
        });
      } else if (!cellAnon) {
        // Sum duplicate rows for the same segment × year.
        entry.byYear.set(year, {
          value: prev.anon ? value : prev.value + value,
          anon: false,
          isFc: prev.isFc || isFc,
        });
      }
    }

    const years = Array.from(yearFc.keys())
      .sort((a, b) => a - b)
      .slice(-MAX_PREVIEW_VALUES);
    if (!years.length || !byCode.size) {
      return [];
    }

    const isFc = years.map((y) => !!yearFc.get(y));
    const lastYear = years[years.length - 1];

    const ranked = Array.from(byCode.entries())
      .map(([code, entry]) => {
        const last = entry.byYear.get(lastYear);
        const sortVal =
          last && !last.anon && Number.isFinite(last.value)
            ? last.value
            : Number.NEGATIVE_INFINITY;
        return { code, entry, sortVal };
      })
      .sort((a, b) => b.sortVal - a.sortVal)
      .slice(0, FORECAST_STACK_TOP_N);

    return ranked.map(({ code, entry }) => {
      const vals: number[] = [];
      const anon: boolean[] = [];
      for (const y of years) {
        const cell = entry.byYear.get(y);
        if (!cell || cell.anon) {
          vals.push(0);
          anon.push(true);
        } else {
          vals.push(cell.value);
          anon.push(false);
        }
      }
      return {
        code,
        name: entry.name,
        vals,
        years,
        anon,
        isFc,
      };
    });
  }

  /**
   * Index-ranking-development slope (NVDuoB): one series per entity (WZ /
   * Teilmarkt), shared years. Ranks use endpoints only; final-year * dropped.
   */
  buildSlopePreview(
    data: DSVRowArray,
    _filterValues?: Map<string, unknown> | Record<string, unknown>,
    dashboardId?: string,
  ): SlicePreviewSeries[] {
    let rows = Array.from(data as Iterable<Record<string, unknown>>);
    if (!rows.length) {
      return [];
    }

    // Prefer line-pass rows when the chart pipeline stamped pass_years_line_chart.
    const hasLinePass = rows.some((r) => 'pass_years_line_chart' in r);
    if (hasLinePass) {
      rows = rows.filter(
        (r) => r['pass_years_line_chart'] === 'true' || r['pass_years_line_chart'] === true,
      );
      if (!rows.length) {
        return [];
      }
    }

    const sample = rows[0];
    const categoryPair = resolveCategoryPair(sample, dashboardId ?? '');
    if (!categoryPair) {
      return [];
    }

    const valueKey = pickValueKey(
      sample,
      categoryPair,
      TIME_KEYS.find((key) => key in sample),
    );
    if (!valueKey) {
      return [];
    }

    type Cell = { value: number; anon: boolean };
    const byCode = new Map<string, { name: string; byYear: Map<number, Cell> }>();
    const yearSet = new Set<number>();

    for (const row of rows) {
      const year = Number(row['year']);
      if (!Number.isFinite(year)) {
        continue;
      }
      const code = `${row[categoryPair.code] ?? ''}`.trim();
      if (!code) {
        continue;
      }
      yearSet.add(year);

      const raw = row[valueKey];
      const cellAnon = isAnonPreviewCell(raw);
      const value = Number(raw);

      let entry = byCode.get(code);
      if (!entry) {
        entry = {
          name: formatCategoryLabel(row, categoryPair, code),
          byYear: new Map(),
        };
        byCode.set(code, entry);
      }

      const prev = entry.byYear.get(year);
      if (!prev) {
        entry.byYear.set(year, {
          value: cellAnon ? 0 : value,
          anon: cellAnon,
        });
      } else if (!cellAnon) {
        entry.byYear.set(year, {
          value: prev.anon ? value : prev.value + value,
          anon: false,
        });
      }
    }

    const years = Array.from(yearSet)
      .sort((a, b) => a - b)
      .slice(-MAX_PREVIEW_VALUES);
    if (years.length < 2 || !byCode.size) {
      return [];
    }

    const lastYear = years[years.length - 1];

    const ranked = Array.from(byCode.entries())
      .map(([code, entry]) => {
        const last = entry.byYear.get(lastYear);
        // Spec: drop entities anonymized at final year.
        if (!last || last.anon || !Number.isFinite(last.value)) {
          return null;
        }
        return { code, entry, sortVal: last.value };
      })
      .filter((x): x is { code: string; entry: { name: string; byYear: Map<number, Cell> }; sortVal: number } => x != null)
      .sort((a, b) => b.sortVal - a.sortVal)
      .slice(0, SLOPE_TOP_N);

    return ranked.map(({ code, entry }) => {
      const vals: number[] = [];
      const anon: boolean[] = [];
      for (const y of years) {
        const cell = entry.byYear.get(y);
        if (!cell || cell.anon) {
          vals.push(0);
          anon.push(true);
        } else {
          vals.push(cell.value);
          anon.push(false);
        }
      }
      return {
        code,
        name: entry.name,
        vals,
        years,
        anon,
      };
    });
  }

  private _resolveForecastFocusId(
    filterValues?: Map<string, unknown> | Record<string, unknown>,
  ): unknown {
    const keys = ['states', 'sub_markets', 'wz_classes', 'state', 'sub_market', 'wz_class'];
    for (const key of keys) {
      const raw = readFilterValue(filterValues, key);
      if (Array.isArray(raw) && raw.length) {
        return raw[0];
      }
      if (raw != null && raw !== '') {
        return raw;
      }
    }
    return undefined;
  }

  private _resolveForecastValueKeys(
    sample: Record<string, unknown>,
    categoryPair: { code: string; name: string } | undefined,
  ): { valueKey: string | null; upperKey: string | null; lowerKey: string | null } {
    const preferredValue = VALUE_KEYS.find((key) => key in sample) ?? null;
    const valueKey =
      preferredValue ??
      pickValueKey(sample, categoryPair, TIME_KEYS.find((key) => key in sample));

    const preferPair = (high: string, low: string) =>
      high in sample && low in sample ? { upperKey: high, lowerKey: low } : null;

    const preferredCi =
      preferPair('value_confidence_interval_high', 'value_confidence_interval_low') ??
      (() => {
        const highs = Object.keys(sample).filter((k) => k.includes('confidence_interval_high'));
        const lows = Object.keys(sample).filter((k) => k.includes('confidence_interval_low'));
        if (!highs.length || !lows.length) {
          return null;
        }
        // Prefer the shortest CI pair (usually the primary metric keys).
        highs.sort((a, b) => a.length - b.length);
        const high = highs[0];
        const prefix = high.replace(/_confidence_interval_high$/, '');
        const low =
          lows.find((k) => k === `${prefix}_confidence_interval_low`) ??
          lows.sort((a, b) => a.length - b.length)[0];
        return { upperKey: high, lowerKey: low };
      })();

    return {
      valueKey,
      upperKey: preferredCi?.upperKey ?? null,
      lowerKey: preferredCi?.lowerKey ?? null,
    };
  }
}
